import { useState, useEffect } from "react";
import { Loader2, Tags, Save, RotateCcw } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";
import {
  fetchSystemSettings,
  updateSystemSettings,
} from "../../services/api";
import { inputStyle, SPACING_DEFAULTS, SPACING_FIELDS } from "./constants";

export default function NewsletterSpacingSection() {
  const [values, setValues] = useState({ ...SPACING_DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => {
        if (s?.newsletterSpacing) setValues({ ...SPACING_DEFAULTS, ...s.newsletterSpacing });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings({ newsletterSpacing: values });
      setSuccess("Spacing saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setValues({ ...SPACING_DEFAULTS });
    setSuccess(null);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Tags size={18} color={colors.accent} />
        <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, margin: 0 }}>
          Newsletter Spacing
        </h3>
      </div>

      <p style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
        Controls the spacing (in pixels) between text elements in newsletters — both in the editor
        and sent emails.
      </p>

      {error && (
        <div style={{ padding: "8px 12px", marginBottom: 12, borderRadius: 6, background: "rgba(239,68,68,0.1)", color: colors.danger, fontSize: 12 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ padding: "8px 12px", marginBottom: 12, borderRadius: 6, background: "rgba(16,185,129,0.1)", color: colors.success, fontSize: 12 }}>
          {success}
        </div>
      )}

      {loading ? (
        <Loader2 size={16} color={colors.textSecondary} className="spin" />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", marginBottom: 16 }}>
            {SPACING_FIELDS.map(({ key, label }) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 12, color: colors.textSecondary, minWidth: 140 }}>{label}</label>
                <input
                  type="number"
                  min={0}
                  max={80}
                  value={values[key]}
                  onChange={(e) => {
                    setValues((v) => ({ ...v, [key]: parseInt(e.target.value, 10) || 0 }));
                    setSuccess(null);
                  }}
                  style={{ ...inputStyle, width: 64, textAlign: "center", padding: "6px 8px" }}
                />
                <span style={{ fontSize: 11, color: colors.textSecondary }}>px</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="primary" size="sm" icon={Save} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="secondary" size="sm" icon={RotateCcw} onClick={handleReset}>
              Reset Defaults
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
